import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type MutateOptions,
} from "react-query";
import { api } from "utils/api";
import { useForm } from "react-hook-form";
import {
  convertLocalizedInputToUTC,
  convertUTCToLocalizedInput,
} from "utils/date";
import type {
  FeedResponse,
  Post,
  BasePostFormFields,
  PostFormFields,
  PostFormFieldsPreTransform,
  SaleTag,
} from "../types";
import { isSalePost } from "../utils/postTypes";
import { postKeys } from "./queryKeys";

/**
 * Get a single post by id
 */
const getPost = (postId?: string) =>
  postId
    ? api.get<Post>(`/post/${postId}`)
    : Promise.reject(new Error("Invalid post id"));

/**
 * Custom hook to get a single post and manage state
 */
const usePost = (postId?: string) =>
  useQuery(postKeys.detail(postId), () => getPost(postId));

/**
 * Get a feed of posts, optionally filtered or paginated
 */
const getFeed = (params?: URLSearchParams, pageParam?: string) => {
  // Create new params object so we don't mutate live params, `undefined` creates empty object
  const merged = new URLSearchParams(params);

  // Add page parameter for infinite query if we have it
  if (pageParam) merged.set("searchAfter", pageParam);

  // Add perPage to params if we don't already have it from the hook arguments.
  // First page request defaults to 48 posts (multiple of 12 for grid), and subsequent
  // requests to load more will just get 12 at a time.
  if (!params?.has("perPage")) merged.set("perPage", pageParam ? "12" : "48");

  // If there are any search params, add them to a query string
  const queryStr = merged.toString() ? `?${merged}` : "";

  return api.get<FeedResponse>(`/feed${queryStr}`);
};

/**
 * Custom hook to get a feed of posts and manage state
 */
const useFeed = (params?: URLSearchParams) =>
  useInfiniteQuery(
    postKeys.list(params),
    ({ pageParam }) => getFeed(params, pageParam),
    {
      getNextPageParam: (lastPage) => lastPage.searchAfter,
    }
  );

/**
 * Create a new post
 */
const createPost = async (formData: PostFormFields) =>
  api.post<Post>("/post", {
    body: JSON.stringify(formData),
  });

/**
 * Custom hook to create a new post and manage state
 */
const useCreatePost = () => useMutation(createPost);

/**
 * Update an existing post
 */
const updatePost = async ({
  id,
  ...formData
}: PostFormFields & { id: string }) =>
  api.put<Post>(`/post/${id}`, {
    body: JSON.stringify(formData),
  });

/**
 * Custom hook to update an existing post and manage state
 */
const useUpdatePost = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (formData: PostFormFields) => updatePost({ id, ...formData }),
    {
      onSuccess: (updatedPost) => {
        // Update the cache with new data
        queryClient.setQueryData(postKeys.detail(updatedPost.id), updatedPost);
        // Invalidate any cached feeds that might contain this post
        queryClient.invalidateQueries(postKeys.lists());
      },
    }
  );
};

const transformPostIntoFields = (post: Post): PostFormFieldsPreTransform => {
  if (isSalePost(post)) {
    const { saleTags, saleStart, saleEnd, ...rest } = post;
    return {
      ...rest,
      saleTags: saleTags[0],
      newDrop: saleTags.includes("New Drop") ? "true" : undefined,
      ...(saleEnd ? { saleEnd: convertUTCToLocalizedInput(saleEnd) } : {}),
      ...(saleStart
        ? { saleStart: convertUTCToLocalizedInput(saleStart) }
        : {}),
    };
  }
  return post;
};

type UsePostFormParams = {
  /**
   * For non-admins, pass in their artist ID to indicate the desired feed destination
   */
  artistId?: string;
  /**
   * Use an existing Post as form's default values when editing
   */
  post?: Post;
  // Optional event handlers from component using the form
} & MutateOptions<Post, unknown, PostFormFields>;

/**
 * Custom hook to handle form validation and submission for creating/updating a post
 */
const usePostForm = ({ post, artistId, onSuccess }: UsePostFormParams) => {
  const { handleSubmit, ...form } = useForm<PostFormFieldsPreTransform>({
    // Fill fields with original post data if we're editing, otherwise set some base defaults for a new post
    defaultValues: post
      ? transformPostIntoFields(post)
      : {
          status: "PUBLISHED",
          postType: "OTHER",
          destinations: [{ feedId: artistId, feedType: "ARTIST" }],
          salePrice: undefined,
          saleCurrency: "",
        },
  });

  // Run both create/edit hooks to create mutate functions and state management
  const createMutation = useCreatePost();
  const updateMutation = useUpdatePost(post?.id ?? "NULL");

  // If we've been given an existing post, use update mutation. Otherwise, use create mutation.
  const isEdit = !!post;
  const mutation = isEdit ? updateMutation : createMutation;

  // Create form submit handler that can handle either update or create mutate calls
  const onSubmit = handleSubmit((formData) => {
    // Form data is in pre-transformed state, we need to pull out base post fields then add in separate specific fields
    const baseFields: BasePostFormFields = {
      title: formData.title,
      text: formData.text,
      status: formData.status,
      destinations: formData.destinations,
    };

    // Add optional fields to baseFields if they exist, otherwise they won't be added to POST body
    if (formData.image?.url) {
      // ImageUploader uses empty string for default url field input. Only add image to Post body
      // if url field has a non-empty string, which means successful upload.
      baseFields.image = formData.image;
    }
    if (formData.link) {
      baseFields.link = formData.link;
    }

    let transformed: PostFormFields;
    if (formData.postType === "SALE") {
      transformed = {
        ...baseFields,
        postType: formData.postType,
        saleEnd: formData.saleEnd
          ? convertLocalizedInputToUTC(formData.saleEnd, true)
          : undefined,
        saleStart: formData.saleStart
          ? convertLocalizedInputToUTC(formData.saleStart)
          : undefined,
        saleTags: [
          // Grab the single string from sale tag select input
          formData.saleTags,
          // And append new drop tag if checkbox is selected, undefined will get filtered out
          formData.newDrop === "true" ? "New Drop" : undefined,
        ].filter((x): x is SaleTag => !!x),
      };

      // Empty string converted to number type is NaN, check before adding to post body
      if (!Number.isNaN(formData.salePrice)) {
        transformed.salePrice = formData.salePrice;
        // Currency is only required if price is entered
        transformed.saleCurrency = formData.saleCurrency;
      }
    } else {
      transformed = {
        ...baseFields,
        postType: formData.postType,
      };
    }

    // If we're using an update mutation, roll form fields into existing post data
    mutation.mutate(isEdit ? { ...post, ...transformed } : transformed, {
      onSuccess,
    });
  });

  return {
    ...form,
    isEdit,
    mutation,
    onSubmit,
  };
};

export { useFeed, usePost, usePostForm, useUpdatePost };
